/**
 * Super-admin v2 shell — section state machine + modal stack.
 * Layout: full-width rounded container (fills viewport, 20px frame) с 240px sidebar + content.
 */
import { lazy, Suspense, useState } from "react";
import { Menu } from "lucide-react";
import { SuperAdminGate } from "./auth/SuperAdminGate";
import { useSuperAdminAuth } from "./auth/SuperAdminAuthProvider";
import { Sidebar, type Section } from "./components/Sidebar";
import type { CafeListRow } from "./screens/CafesScreen";
import { CreateCafeWizard } from "./components/CreateCafeWizard";
import { CafeDetailModal } from "./components/CafeDetailModal";
import { ResetPasswordModal } from "./components/ResetPasswordModal";
import { C, FONT } from "./styles/theme";

// Экраны — lazy (named exports → .then(default)): без этого dnd-kit + recharts
// + papaparse грузились одним 595КБ index-чанком на login-гейте.
const DashboardScreen = lazy(() => import("./screens/DashboardScreen").then((m) => ({ default: m.DashboardScreen })));
const CafesScreen     = lazy(() => import("./screens/CafesScreen").then((m) => ({ default: m.CafesScreen })));
const CrmScreen       = lazy(() => import("./screens/CrmScreen").then((m) => ({ default: m.CrmScreen })));
const FinanceScreen   = lazy(() => import("./screens/FinanceScreen").then((m) => ({ default: m.FinanceScreen })));
const AnalyticsScreen = lazy(() => import("./screens/AnalyticsScreen").then((m) => ({ default: m.AnalyticsScreen })));
const AnnouncementsScreen = lazy(() => import("./screens/AnnouncementsScreen").then((m) => ({ default: m.AnnouncementsScreen })));
// 00434: платформенная база типовых блюд (кафе добавляют их через «Из базы»).
const DishLibraryScreen = lazy(() => import("./screens/DishLibraryScreen").then((m) => ({ default: m.DishLibraryScreen })));
const SettingsScreen  = lazy(() => import("./screens/SettingsScreen").then((m) => ({ default: m.SettingsScreen })));
// SA-6 (docs/SUPER-ADMIN-AUDIT.md): экран был написан, но не подключён к Shell —
// 3D-загрузчик меню (superAdminUploadMenuModel) был недостижим из UI.
const TenantDetailScreen = lazy(() => import("./screens/TenantDetailScreen").then((m) => ({ default: m.TenantDetailScreen })));

export function App() {
  return (
    <SuperAdminGate>
      <Shell />
    </SuperAdminGate>
  );
}

type ModalState =
  | null
  | { kind: "createCafe" }
  | { kind: "cafeDetail"; cafe: CafeListRow }
  | { kind: "resetPassword"; cafe: CafeListRow; staffId: string };

function Shell() {
  const { signOut, status } = useSuperAdminAuth();
  const [section, setSection] = useState<Section>("dashboard");
  const [modal, setModal] = useState<ModalState>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // SA-6: полноэкранная карточка тенанта (3D-модели меню) поверх секций
  const [tenantDetailSlug, setTenantDetailSlug] = useState<string | null>(null);
  // Мобильный drawer (≤768px): сайдбар скрыт за экраном, открывается бургером.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const email = status.kind === "authenticated" ? status.user.email ?? "" : "";

  return (
    <div className="sa-root" style={{
      minHeight: "100vh",
      background: "#000",
      padding: 20,
      fontFamily: FONT.body,
    }}>
      {/* Бургер — только на мобиле (CSS), поверх карточки */}
      <button
        type="button"
        className="sa-burger"
        aria-label="Меню"
        onClick={() => setMobileNavOpen(true)}
        style={{
          position: "fixed", top: 12, left: 12, zIndex: 130,
          width: 40, height: 40, borderRadius: 10,
          background: "#0A0E14", color: C.text,
          border: `1px solid ${C.border}`,
          cursor: "pointer",
          alignItems: "center", justifyContent: "center",
        }}
      >
        <Menu size={18} />
      </button>
      {mobileNavOpen && (
        <div
          className="sa-overlay"
          onClick={() => setMobileNavOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 110 }}
        />
      )}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div className="sa-card" style={{
          width: "100%",
          background: C.bg,
          borderRadius: 24,
          overflow: "hidden",
          color: C.text,
          boxShadow: "0 30px 80px rgba(0,0,0,0.8)",
          border: `1px solid ${C.border}`,
          minHeight: 600,
          display: "flex",
          position: "relative",
        }}>
          <Sidebar
            section={section}
            setSection={(s) => {
              setSection(s);
              setMobileNavOpen(false);
            }}
            userEmail={email}
            onLogout={signOut}
            mobileOpen={mobileNavOpen}
          />

          <div key={reloadKey} className="sa-content" style={{ flex: 1, overflow: "auto", minWidth: 0 }}>
            <Suspense fallback={
              <div style={{ display: "grid", placeItems: "center", minHeight: "60vh", color: "#888", fontSize: 14 }}>
                Загрузка…
              </div>
            }>
            {tenantDetailSlug && (
              <TenantDetailScreen
                tenantSlug={tenantDetailSlug}
                onBack={() => setTenantDetailSlug(null)}
              />
            )}
            {!tenantDetailSlug && section === "dashboard" && (
              <DashboardScreen onCreateCafe={() => setModal({ kind: "createCafe" })} />
            )}
            {!tenantDetailSlug && section === "cafes" && (
              <CafesScreen
                onCreateCafe={() => setModal({ kind: "createCafe" })}
                onSelectCafe={(cafe) => setModal({ kind: "cafeDetail", cafe })}
              />
            )}
            {!tenantDetailSlug && section === "crm" && <CrmScreen />}
            {!tenantDetailSlug && section === "finance" && <FinanceScreen />}
            {!tenantDetailSlug && section === "analytics" && <AnalyticsScreen />}
            {!tenantDetailSlug && section === "news" && <AnnouncementsScreen />}
            {!tenantDetailSlug && section === "library" && <DishLibraryScreen />}
            {!tenantDetailSlug && section === "settings" && <SettingsScreen />}
            </Suspense>
          </div>

          {modal?.kind === "createCafe" && (
            <CreateCafeWizard
              onClose={() => setModal(null)}
              onCreated={() => setReloadKey((k) => k + 1)}
            />
          )}
          {modal?.kind === "cafeDetail" && (
            <CafeDetailModal
              cafe={modal.cafe}
              onClose={() => setModal(null)}
              onResetPassword={(staffId) =>
                setModal({ kind: "resetPassword", cafe: modal.cafe, staffId })
              }
              onActionDone={() => setReloadKey((k) => k + 1)}
              onOpenTenantDetail={(slug) => {
                setModal(null);
                setTenantDetailSlug(slug);
              }}
            />
          )}
          {modal?.kind === "resetPassword" && (
            <ResetPasswordModal
              tenantId={modal.cafe.id}
              tenantName={modal.cafe.name}
              staffId={modal.staffId}
              onClose={() => setModal({ kind: "cafeDetail", cafe: modal.cafe })}
            />
          )}
        </div>
      </div>
    </div>
  );
}
